import { useContext, useEffect } from 'react';

import { FeatureFlags } from 'featureFlags/constants/featureFlags';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { getMParticleDeviceId } from 'tracking/utils/getMparticleDeviceId';
import { useAuthState } from 'auth/hooks/useAuth';
import { useFeatureFlag } from 'shared/hooks';
import {
  ExperimentStateContext,
  ExperimentDispatchContext,
} from 'experiments/providers/ExperimentProvider';
import { ExperimentActions } from 'experiments/state/ExperimentReducer';
import { fetchExperimentData } from 'experiments/services/fetchExperimentData';

/**
 * @name useExperimentGroupName
 * @description Returns the groupName for this user as a string
 * @param {string} experimentName
 *
 * @returns {string} experimentGroupName
 */
export const useExperimentGroupName = (experimentName: string) => {
  const allowExperiments = useFeatureFlag(FeatureFlags.WEB_ALLOW_EXPERIMENTS);
  const { isAuthenticated, user } = useAuthState();
  const deviceId = getMParticleDeviceId();
  const userId: string = isAuthenticated ? user?.id : deviceId;
  const experimentState = useContext(ExperimentStateContext);
  const experimentDispatch = useContext(ExperimentDispatchContext);

  useEffect(() => {
    if (!allowExperiments) return;
    if (!userId) return;
    if (experimentState[experimentName]) return;
    const fetchData = async () => {
      const data = await fetchExperimentData({ experimentName, userId });

      experimentDispatch({
        type: ExperimentActions.setExperiment,
        payload: {
          experimentName,
          groupAssigned: data?.group?.name,
        },
      });
    };

    fetchData().catch(bugsnag);
  }, [allowExperiments, experimentName, userId, experimentState, experimentDispatch]);

  // if a user is not logged in, and we cannot get deviceId from mParticle, we will not return a group
  return userId ? experimentState[experimentName] : null;
};
